import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  Clock,
  DatabaseZap,

  HelpCircle,
  LineChart,
  Megaphone,
  MessageCircle,
  PlugZap,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { WHATSAPP_LINK } from '../constants';
import useCaseLandingContent from '../content/use-case-landings.json';
import { POSITIONING } from '../content/positioning';
import { MarketingHead, marketingCanonicalUrl } from '../components/marketing/MarketingHead';

type UseCaseKey = 'clinicas' | 'estetica' | 'ventas' | 'integraciones';

type UseCase = {
  key: UseCaseKey;
  path: string;
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  cta: string;
  whatsappText: string;
  heroImage: string;
  heroImageAlt: string;
  who: string[];
  prioritySteps: Array<{ title: string; description: string }>;
  outcomes: string[];
  modules: Array<{ title: string; description: string; icon: LucideIcon }>;
  integrations: string[];
  integrationGuides?: Array<{ label: string; href: string }>;
  relatedStories: Array<{ title: string; description: string; href: string; tag: string; image: string; imageAlt: string }>;
  featuredProof?: Array<{ title: string; description: string; href: string; tag: string; image: string; imageAlt: string }>;
  processExample?: Array<{ title: string; description: string }>;
  integrationMatrix?: Array<{ software: string; firstUseCase: string; phase: string; href?: string }>;
  proof: string;
  faq: Array<{ q: string; a: string }>;
};

const EVA_CAUSIN_CASE = {
  title: 'Centro Eva Causín',
  description: 'Flowww conectado con citas, lista de espera, requerimientos, derivación profesional, seguimiento post cita, recuperación de conversaciones, Instagram y atención de dudas.',
  href: '/blog/caso-exito-centro-eva-causin-flowww-whatsapp.html',
  tag: 'Centro de estética + Flowww',
  image: '/img/centro-eva-causin-interior-marca.webp',
  imageAlt: 'Interior del Centro Eva Causín en Madrid',
};

const CONSENTIRME_CASE = {
  title: 'Consentirme',
  description: 'Estética, reservas en Treatwell/Booksy y WhatsApp conectados sin cambiar la operativa del centro.',
  href: '/blog/caso-exito-consentirme-estetica-treatwell-whatsapp.html',
  tag: 'Estética + reservas',
  image: '/img/consentirme-treatwell-1.jpg',
  imageAlt: 'Fachada del centro de estética Consentirme',
};

const SEVEN_SISTERS_INTEGRATIONS_CASE = {
  title: 'Seven Sisters Tattoo',
  description: 'WhatsApp conectado con la gestión del estudio para reunir documentación, recordatorios y seguimiento en un mismo flujo.',
  href: '/blog/caso-exito-seven-sisters-tattoo-madrid.html',
  tag: 'Gestión + WhatsApp',
  image: '/img/seven-sisters-exterior.jpg',
  imageAlt: 'Exterior del estudio Seven Sisters Tattoo en Madrid',
};

const SANCHEZ_BARBERIA_CASE = {
  title: 'Sánchez Barbería',
  description: 'Voz y WhatsApp conectados con Booksy, manteniendo la agenda de referencia de la barbería.',
  href: '/blog/caso-exito-sanchez-barberia-booksy-agente-voz.html',
  tag: 'Barbería + Booksy',
  image: '/img/sanchez-barberia-fachada-completa.jpg',
  imageAlt: 'Fachada de Sánchez Barbería',
};

export const USE_CASES: Record<UseCaseKey, UseCase> = {
  clinicas: {
    key: 'clinicas',
    ...useCaseLandingContent.clinicas,
    heroImage: '/clinica-agenda-ia.png',
    heroImageAlt: 'Recepción clínica con agenda, WhatsApp y automatización IA conectados',
    who: ['Fisioterapia', 'Psicología', 'Nutrición', 'Clínicas privadas', 'Centros de bienestar', 'Servicios recurrentes'],
    prioritySteps: [
      {
        title: 'Llamadas y dudas repetidas',
        description: 'La IA filtra horarios, precios orientativos, servicios y preguntas frecuentes antes de escalar al equipo.',
      },
      {
        title: 'Recordatorios y no-shows',
        description: 'Confirmaciones, cambios y avisos por WhatsApp para reducir ausencias y huecos improductivos.',
      },
      {
        title: 'Huecos libres y lista de espera',
        description: 'Cuando alguien cancela, el sistema ayuda a priorizar a quién avisar para recuperar agenda.',
      },
    ],
    outcomes: [
      'Mantener atendidas las dudas frecuentes por WhatsApp',
      'Coordinar reservas, cambios y cancelaciones según permisos',
      'Preparar o enviar recordatorios y confirmaciones autorizadas',
      'Mantener visible la lista de espera para aprovechar huecos libres',
      'Escalar al equipo humano cuando haga falta criterio profesional',
    ],
    modules: [
      { title: 'Atención WhatsApp y voz', description: 'La IA responde primeras consultas, precios orientativos, disponibilidad y derivaciones al equipo.', icon: MessageCircle },
      { title: 'Agenda conectada', description: 'Conexión con calendario, CRM o software de reservas para evitar doble trabajo.', icon: CalendarCheck },
      { title: 'Recordatorios y recuperación', description: 'Confirmaciones, cambios, reprogramaciones y avisos para llenar huecos.', icon: Clock },
      { title: 'Panel de seguimiento', description: 'Visibilidad de conversaciones, oportunidades y próximas acciones.', icon: LineChart },
    ],
    integrations: ['Google Calendar', 'TIMP', 'Flowww', 'CRM', 'Gmail', 'WhatsApp Business API'],
    relatedStories: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SANCHEZ_BARBERIA_CASE],
    featuredProof: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SANCHEZ_BARBERIA_CASE],
    proof: 'Ideal si ahora dependes de responder manualmente WhatsApp, llamadas o mensajes de Instagram para cerrar reservas.',
  },
  estetica: {
    key: 'estetica',
    ...useCaseLandingContent.estetica,
    heroImage: '/img/centro-eva-causin-interior-marca.webp',
    heroImageAlt: 'Interior del Centro Eva Causín en Madrid',
    who: ['Centros de estética', 'Clínicas estéticas', 'Peluquerías', 'Barberías', 'Uñas y cejas', 'Masajes y bienestar'],
    prioritySteps: [
      {
        title: 'WhatsApp mientras el equipo atiende',
        description: 'Respuestas iniciales, servicios, disponibilidad y derivación a una persona cuando hace falta criterio.',
      },
      {
        title: 'Reservas y recordatorios',
        description: 'Conexión o coordinación con Flowww, Booksy, Treatwell, agenda propia o calendario para evitar seguimiento manual.',
      },
      {
        title: 'Reseñas y reactivación',
        description: 'Pedir reseñas tras la visita y preparar campañas aprobadas para clientas inactivas o servicios concretos.',
      },
    ],
    outcomes: [
      'Responder por WhatsApp aunque el equipo esté atendiendo',
      'Coordinar reservas con el software disponible según acceso',
      'Pedir una reseña tras la cita',
      'Preparar campañas aprobadas para clientas inactivas',
      'Pedir una reseña tras la cita con enlace a Google',
    ],
    modules: [
      { title: 'Reservas por WhatsApp', description: 'Primer filtro, disponibilidad, cambios y recordatorios conectados a la operativa.', icon: CalendarCheck },
      { title: 'Marketing IA', description: 'Ideas, posts, campañas y reactivación basadas en servicios, temporada y oportunidades reales.', icon: Megaphone },
      { title: 'Reseñas y reputación', description: 'Solicitud de reseña tras la cita con enlace a Google.', icon: Star },
      { title: 'Integraciones de belleza', description: 'Trabajo alrededor de Flowww, Booksy, Treatwell, agenda propia, agenda propia.', icon: PlugZap },
    ],
    integrations: ['Flowww', 'Booksy', 'Treatwell', 'TIMP', 'Instagram', 'Google Business Profile', 'WhatsApp', 'Google Calendar'],
    relatedStories: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SANCHEZ_BARBERIA_CASE],
    featuredProof: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SANCHEZ_BARBERIA_CASE],
    proof: 'Pensada para centros donde cada mensaje no respondido puede acabar siendo una reserva perdida.',
  },
  ventas: {
    key: 'ventas',
    ...useCaseLandingContent.ventas,
    heroImage: '/persona-grupo-reunion.webp',
    heroImageAlt: 'Equipo revisando consultas y próximos pasos comerciales',
    who: ['Negocios que reciben consultas por WhatsApp', 'Servicios que preparan presupuestos', 'Academias y centros', 'Equipos comerciales pequeños'],
    prioritySteps: [
      {
        title: 'Recibe las consultas',
        description: 'Reúne los mensajes de WhatsApp, Instagram, email y formularios.',
      },
      {
        title: 'Ordena la información',
        description: 'Resume qué busca cada persona y qué datos faltan para poder avanzar.',
      },
      {
        title: 'Deja claro el siguiente paso',
        description: 'Indica qué conversación necesita respuesta y recuerda cuándo retomarla.',
      },
    ],
    outcomes: [
      'Consultas reunidas en un mismo lugar',
      'Información clara sobre cada persona',
      'Siguiente paso visible para el equipo',
      'Avisos para no olvidar el seguimiento',
    ],
    modules: [
      { title: 'Consultas reunidas', description: 'Los mensajes de tus canales llegan al mismo proceso.', icon: Users },
      { title: 'Información clara', description: 'El equipo ve qué busca cada persona sin releer toda la conversación.', icon: Bot },
      { title: 'Seguimiento', description: 'Quedan visibles las respuestas pendientes y los recordatorios.', icon: Clock },
    ],
    integrations: ['WhatsApp', 'Instagram', 'Email', 'Formularios', 'CRM'],
    relatedStories: [],
    featuredProof: [],
    proof: 'Útil cuando las consultas llegan por varios canales y cuesta saber quién espera respuesta.',
  },
  integraciones: {
    key: 'integraciones',
    ...useCaseLandingContent.integraciones,
    heroImage: '/equipo-colaborando.webp',
    heroImageAlt: 'Equipo conectando herramientas, datos e integraciones para automatizar procesos',
    who: ['Negocios con software propio', 'Centros con agenda', 'Equipos con CRM', 'Agenda propia', 'Negocios con varias plataformas', 'Empresas que no quieren migrar'],
    prioritySteps: [
      {
        title: 'Mapa de herramientas actuales',
        description: 'Canales, agenda, CRM, hojas de cálculo, email y puntos donde hoy se copia información a mano.',
      },
      {
        title: 'Primer caso con retorno rápido',
        description: 'Elegimos un flujo acotado: WhatsApp + agenda, lead + CRM, solicitud de reseña tras la cita o seguimiento en el CRM propio.',
      },
      {
        title: 'Añadir por fases según impacto',
        description: 'Cuando el primer módulo funciona, conectamos más canales sin obligarte a migrar de plataforma.',
      },
    ],
    outcomes: [
      'Mantener el software actual siempre que sea posible',
      'Conectar WhatsApp con agenda, CRM o base de datos',
      'Evitar copiar datos manualmente entre herramientas',
      'Crear automatizaciones a medida alrededor de la operativa',
      'Medir conversiones, campañas y oportunidades reales',
    ],
    modules: [
      { title: 'Mapeo de operativa', description: 'Analizamos canales, herramientas, datos y puntos donde se pierde tiempo o venta.', icon: DatabaseZap },
      { title: 'Conectores', description: 'APIs, webhooks, automatizaciones, Gmail, CRM propio, calendarios y herramientas verticales.', icon: PlugZap },
      { title: 'Capa IA', description: 'Atención, clasificación, resúmenes, recomendaciones y generación de contenido.', icon: Bot },
      { title: 'Control y medición', description: 'Logs, paneles, métricas y alertas para saber qué funciona.', icon: LineChart },
    ],
    integrations: ['Flowww', 'TIMP', 'Booksy', 'Treatwell', 'Google Calendar', 'WhatsApp', 'Instagram', 'Solicitudes de reseña en Google', 'Gmail', 'Stripe', 'APIs'],
    relatedStories: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SEVEN_SISTERS_INTEGRATIONS_CASE, SANCHEZ_BARBERIA_CASE],
    featuredProof: [EVA_CAUSIN_CASE, CONSENTIRME_CASE, SEVEN_SISTERS_INTEGRATIONS_CASE, SANCHEZ_BARBERIA_CASE],
    integrationMatrix: [
      {
        software: 'Flowww',
        firstUseCase: 'WhatsApp, reservas, campañas y reactivación para estética o clínicas.',
        phase: 'Atención y recordatorios; después, campañas segmentadas.',
        href: '/blog/integracion-flowww-whatsapp-clinicas-estetica.html',
      },
      {
        software: 'Treatwell',
        firstUseCase: 'Reservas por WhatsApp conectadas a tu agenda de Treatwell.',
        phase: 'Cada integración se configura para tu negocio: cuéntanos cómo trabajas y te decimos plazo y precio.',
        href: '/blog/integracion-treatwell-whatsapp-reservas.html',
      },
      {
        software: 'TIMP',
        firstUseCase: 'Reservas y recordatorios conectados a tu agenda de TIMP.',
        phase: 'Cada integración se configura para tu negocio: cuéntanos cómo trabajas y te decimos plazo y precio.',
        href: '/blog/integracion-timp-whatsapp-reservas.html',
      },
      {
        software: 'Booksy',
        firstUseCase: 'Reservas y seguimiento WhatsApp para belleza, barberías y estética.',
        phase: 'Primero confirmación y cambios; después reseñas y clientes inactivos.',
        href: '/blog/integracion-booksy-whatsapp-reservas.html',
      },
      {
        software: 'Agenda y CRM propios',
        firstUseCase: 'CRM ligero, disponibilidad, tareas comerciales y medición básica.',
        phase: 'Ideal para validar rápido antes de una integración más profunda.',
      },
    ],
    proof: 'La ventaja no es tener “otro bot”, sino hacer que las herramientas que ya usas funcionen juntas.',
  },
};

const canonicalUseCasePath = (path: string) => `${path.replace(/\/$/, '')}/`;

export const LANDING_LINKS = Object.values(USE_CASES).map(({ key, path, eyebrow, title, description }) => ({
  key,
  path: canonicalUseCasePath(path),
  eyebrow,
  title,
  description,
}));

const SalesLandingSections: React.FC<{ useCase: UseCase; whatsappHref: string }> = ({ useCase, whatsappHref }) => (
  <>
    <section id="como-funciona" className="solution-section solution-section--canvas solution-priority sales-simple-section">
      <div className="solution-container">
        <div className="solution-split">
          <div className="solution-intro">
            <span className="solution-eyebrow">Así funciona</span>
            <h2>Tres pasos. Nada más.</h2>
            <p>Áncora organiza las consultas para que tu equipo sepa qué atender y qué hacer después.</p>
          </div>
          <div className="solution-sequence">
            {useCase.prioritySteps.map((step, index) => (
              <div key={step.title} className="solution-sequence__item">
                <div className="solution-sequence__row">
                  <div className="solution-index">{String(index + 1).padStart(2, '0')}</div>
                  <div className="solution-sequence__copy">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="solution-section solution-section--mint solution-outcomes sales-simple-section">
      <div className="solution-container">
        <div className="solution-section__intro">
          <span className="solution-eyebrow">Para tu equipo</span>
          <h2>Todo lo importante, a la vista</h2>
          <p>Menos conversaciones sueltas. Más claridad para responder y hacer seguimiento.</p>
        </div>
        <div className="solution-outcomes__grid">
          {useCase.outcomes.map((item) => (
            <div key={item} className="solution-outcomes__item">
              <CheckCircle2 size={22} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="use-case-integrations solution-section solution-section--canvas solution-integrations sales-simple-section">
      <div className="solution-container">
        <div className="solution-split">
          <div className="solution-intro">
            <span className="solution-eyebrow">Conexiones</span>
            <h2>Trabaja con tus canales actuales</h2>
            <p>Empezamos por lo que ya utilizas. No necesitas cambiar todas tus herramientas.</p>
            <Link
              to="/integraciones-software-ia/"
              data-marketing-event="integration_click"
              className="solution-inline-link"
            >
              Ver software e integraciones
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="use-case-integrations__stack">
            <p className="use-case-integrations__status">Canales y herramientas</p>
            <div className="use-case-integrations__list">
              {useCase.integrations.map((integration) => (
                <span key={integration}>{integration}</span>
              ))}
            </div>
            <p className="use-case-integrations__disclaimer">Las conexiones dependen de los accesos y permisos disponibles.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="solution-section solution-section--mint solution-faq sales-simple-section">
      <div className="solution-container solution-container--narrow">
        <div className="solution-section__intro">
          <span className="solution-eyebrow">Preguntas habituales</span>
          <h2>Respuestas breves</h2>
        </div>
        <div className="solution-faq__grid">
          {useCase.faq.map((item) => (
            <details key={item.q} className="solution-faq__item">
              <summary>
                <HelpCircle aria-hidden="true" size={21} />
                <span>{item.q}</span>
                <ChevronDown aria-hidden="true" size={19} />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="solution-section solution-section--canvas solution-contact sales-simple-section">
      <div className="solution-container">
        <div className="solution-cta">
          <h2>¿Quieres ordenar mejor tus consultas?</h2>
          <p>Cuéntanos por dónde llegan y cómo haces ahora el seguimiento.</p>
          <a
            href={whatsappHref}
            data-marketing-event="pricing_cta"
            target="_blank"
            rel="noreferrer"
            className="marketing-button marketing-button--primary"
          >
            <MessageCircle size={20} />
            {useCase.cta}
          </a>
        </div>
      </div>
    </section>
  </>
);

export const UseCaseLanding: React.FC<{ useCase: UseCase }> = ({ useCase }) => {
  const whatsappHref = `${WHATSAPP_LINK}?text=${encodeURIComponent(useCase.whatsappText)}`;
  const routeSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: useCase.title,
      description: useCase.seoDescription,
      url: marketingCanonicalUrl(useCase.path),
      provider: {
        '@type': 'Organization',
        name: 'Áncora Automations',
        url: 'https://www.ancoraautomations.es/',
      },
      areaServed: 'ES',
      serviceType: useCase.eyebrow,
    }),
    [useCase],
  );

  return (
    <main className="marketing-shell solution-shell" data-surface={`marketing-${useCase.key}`}>
      <MarketingHead
        title={useCase.seoTitle}
        description={useCase.seoDescription}
        path={useCase.path}
        imagePath={useCase.heroImage}
        imageAlt={useCase.heroImageAlt}
        schema={routeSchema}
      />
      <section className="use-case-hero" aria-labelledby={`use-case-title-${useCase.key}`}>
        <div className="use-case-hero__video" aria-hidden="true">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
            onLoadedData={(event) => {
              event.currentTarget.play().catch(() => undefined);
            }}
          >
            <source src="/hero-video.mp4" type="video/mp4" media="(min-width: 768px)" />
          </video>
        </div>
        <div className="marketing-grid use-case-hero__grid">
          <div className="use-case-hero__copy">
            <p className="marketing-eyebrow marketing-eyebrow--dark">{useCase.eyebrow}</p>
            <h1 id={`use-case-title-${useCase.key}`}>{useCase.title}</h1>
            <p className="use-case-hero__highlight">{useCase.highlight}</p>
            {useCase.key !== 'clinicas' && useCase.key !== 'estetica' && <p className="use-case-hero__lead">{useCase.description}</p>}
            <div className="use-case-hero__actions">
              <a
                href={whatsappHref}
                data-marketing-event="hero_system_cta"
                target="_blank"
                rel="noreferrer"
                className="marketing-button marketing-button--primary"
              >
                <MessageCircle size={20} />
                {useCase.cta}
              </a>
              <a
                href="#como-funciona"
                className="marketing-button marketing-button--secondary"
                data-marketing-event="process_demo"
              >
                {useCase.key === 'ventas' ? 'Ver cómo funciona' : 'Ver módulos'}
                <ArrowRight size={18} />
              </a>
            </div>
            {(useCase.key === 'clinicas' || useCase.key === 'estetica') && <p className="use-case-hero__lead">{useCase.description}</p>}
          </div>
          <aside className="use-case-hero__proof" aria-label="Aplicación en una operativa real">
            <div className="use-case-hero__image">
              <img
                src={useCase.heroImage}
                alt={useCase.heroImageAlt}
                loading="eager"
                width="1200"
                height="805"
              />
            </div>
            <div className="use-case-hero__proof-copy">
              <p className="use-case-hero__proof-label">
                <Star size={15} />
                Punto de partida real
              </p>
              <p>{useCase.proof}</p>
              <ul aria-label="Negocios a los que se aplica">
                {useCase.who.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {useCase.key === 'ventas' ? (
        <SalesLandingSections useCase={useCase} whatsappHref={whatsappHref} />
      ) : useCase.key === 'clinicas' || useCase.key === 'estetica' ? (
        <>
          <section className="use-case-integrations solution-section solution-section--canvas solution-integrations">
            <div className="solution-container">
              <div className="solution-section__intro"><h2>Funciona con</h2></div>
              <div className="use-case-modules__grid">
                <article><h3><a href="/blog/integracion-booksy-whatsapp-reservas.html">Booksy</a></h3><p>Disponibilidad, alta de cliente y creación, modificación y cancelación de citas.</p></article>
                <article><h3><a href="/blog/integracion-flowww-whatsapp-clinicas-estetica.html">Flowww</a></h3><p>Clientes, servicios, bonos y gestión de citas sobre la agenda del centro.</p></article>
                <article><h3><a href="/blog/integracion-treatwell-whatsapp-reservas.html">Treatwell</a></h3><p>Reservas por WhatsApp conectadas a tu agenda de Treatwell. Cada integración se configura para tu negocio: cuéntanos cómo trabajas y te decimos plazo y precio.</p></article>
                <article><h3><a href="/blog/integracion-timp-whatsapp-reservas.html">TIMP</a></h3><p>Conectamos tu agenda de TIMP con WhatsApp.</p></article>
                <article><h3>Agenda propia</h3><p>Agenda y CRM de Áncora para trabajar sin software de reservas externo.</p></article>
              </div>
              <p className="use-case-integrations__disclaimer"><a href="https://wa.me/34614046352?text=Hola%2C%20uso%20otro%20programa%20de%20reservas%20y%20quiero%20conectarlo%20con%20WhatsApp">¿Usas otro programa de reservas? Desarrollamos la conexión para tu negocio.</a></p>
            </div>
          </section>
          <section className="solution-section solution-section--mint solution-related">
            <div className="solution-container">
              <div className="solution-section__intro"><h2>Casos reales del sector</h2></div>
              <div className="solution-featured-proof__stories">
                {useCase.featuredProof?.map((story) => (
                  <a key={story.href} href={story.href} data-marketing-event="case_click" className="solution-story">
                    <span className="solution-story__media"><picture>{useCase.key === 'integraciones' && <source type="image/webp" srcSet={story.image.replace(/\.jpg$/, '\.webp')} />}<img src={story.image} alt={story.imageAlt} loading="lazy" /></picture></span>
                    <div className="solution-story__body"><h3>{story.title}</h3><p>{story.description}</p><span className="solution-story__link">Ver caso real <ArrowRight size={16} /></span></div>
                  </a>
                ))}
              </div>
            </div>
          </section>
          <section id="como-funciona" className="use-case-modules solution-section solution-section--canvas solution-modules">
            <div className="solution-container">
              <div className="solution-section__intro"><h2>Cuatro capacidades probadas</h2></div>
              <div className="use-case-modules__grid">
                <article className="use-case-modules__item"><h3>Recordatorios</h3><p>Confirmaciones y recordatorios por WhatsApp antes de la cita.</p></article>
                <article className="use-case-modules__item"><h3>Lista de espera y adelantar cita</h3><p>Ofrece un hueco disponible a quienes esperan o quieren adelantar su visita.</p></article>
                <article className="use-case-modules__item"><h3>Consentimientos digitales</h3><p>Documentos y firma vinculados a la cita, cuando el servicio los requiere.</p></article>
                <article className="use-case-modules__item"><h3>Paso a una persona</h3><p>El equipo recibe el contexto para resolver excepciones y dudas profesionales.</p></article>
              </div>
            </div>
          </section>
          <section className="solution-section solution-section--mint solution-priority"><div className="solution-container"><h2>Desde 9,99 EUR/mes</h2><p>WhatsApp Base. Las integraciones de agenda y otros módulos se presupuestan según el alcance; no están incluidos automáticamente en este importe.</p><a href="/condiciones-comerciales.html">Ver condiciones comerciales</a></div></section>
          <section className="solution-section solution-section--canvas solution-faq">
            <div className="solution-container solution-container--narrow"><div className="solution-section__intro"><h2>Preguntas frecuentes</h2></div><div className="solution-faq__grid">{useCase.faq.map((item) => <details key={item.q} className="solution-faq__item"><summary><HelpCircle aria-hidden="true" size={21} /><span>{item.q}</span><ChevronDown aria-hidden="true" size={19} /></summary><p>{item.a}</p></details>)}</div></div>
          </section>
          <section className="solution-section solution-section--mint solution-contact"><div className="solution-container"><div className="solution-cta"><h2>Conecta las citas de tu centro con WhatsApp</h2><p>Cuéntanos qué agenda utilizas y revisamos el alcance contigo.</p><a href={whatsappHref} data-marketing-event="pricing_cta" className="marketing-button marketing-button--primary"><MessageCircle size={20} />Hablar por WhatsApp</a></div></div></section>
        </>
      ) : (
        <>
      <section className="use-case-process solution-section solution-section--canvas" aria-labelledby={`operational-process-${useCase.key}`}>
        <div className="marketing-grid use-case-process__grid">
          <div className="use-case-process__intro">
            <p className="marketing-eyebrow">Una gestión completa</p>
            <h2 id={`operational-process-${useCase.key}`}>Del mensaje al siguiente paso resuelto</h2>
            <p>
              Áncora conserva el contexto para que el proceso avance y entrega al equipo las decisiones que necesitan criterio.
            </p>
          </div>
          <ol className="use-case-process__rail">
            {POSITIONING.process.map((step, index) => (
              <li key={step.key} style={{ '--step-index': index } as React.CSSProperties}>
                <span className="use-case-process__node">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{step.label}</h3>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={`solution-section solution-section--mint solution-priority${useCase.key === 'integraciones' ? ' solution-integration-cases' : ''}`}>
        <div className="solution-container">
          <div className="solution-split">
            <div className="solution-intro">
              <span className="solution-eyebrow">Qué automatizamos primero</span>
              <h2>Empezamos por el flujo con más impacto</h2>
              <p>
                No hace falta cambiar todo de golpe. Priorizamos por fases según impacto: primero el punto que más llamadas, huecos o leads está perdiendo.
              </p>
            </div>
            <div className="solution-sequence">
              {useCase.prioritySteps.map((step, index) => (
                <div key={step.title} className="solution-sequence__item">
                  <div className="solution-sequence__row">
                    <div className="solution-index">{String(index + 1).padStart(2, '0')}</div>
                    <div className="solution-sequence__copy">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {useCase.featuredProof && useCase.featuredProof.length > 0 && (
            <div className="solution-featured-proof">
              <div className="solution-featured-proof__layout">
                <div className="solution-featured-proof__intro">
                  <span className="solution-eyebrow">Prueba social cerca del problema</span>
                  <h3>Casos reales antes de decidir</h3>
                  <p>
                    Si tu centro se parece a alguno de estos escenarios, podemos empezar por una fase pequeña y medir impacto antes de ampliar.
                  </p>
                </div>
                <div className="solution-featured-proof__stories">
                  {useCase.featuredProof.map((story) => (
                    <a
                      key={story.href}
                      href={story.href}
                      data-marketing-event="case_click"
                      className="solution-story"
                    >
                      <span className="solution-story__media">
                        <picture>{useCase.key === 'integraciones' && <source type="image/webp" srcSet={story.image.replace(/\.jpg$/, '\.webp')} />}<img src={story.image} alt={story.imageAlt} loading="lazy" /></picture>
                      </span>
                      <div className="solution-story__body">
                        <h4>{story.title}</h4>
                        <p>{story.description}</p>
                        <span className="solution-story__link">
                          Ver caso real
                          <ArrowRight size={16} />
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {useCase.key !== 'integraciones' && (
        <section className="solution-section solution-section--canvas solution-outcomes">
          <div className="solution-container">
            <div className="solution-section__intro">
              <span className="solution-eyebrow">Resultado práctico</span>
              <h2>Qué automatizamos en este caso</h2>
              <p>No vendemos una herramienta genérica: diseñamos el flujo alrededor de cómo trabaja tu negocio.</p>
            </div>
            <div className="solution-outcomes__grid">
              {useCase.outcomes.map((item) => (
                <div key={item} className="solution-outcomes__item">
                  <CheckCircle2 size={22} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {useCase.processExample && useCase.processExample.length > 0 && (
        <section className="solution-section solution-section--mint solution-process-example">
          <div className="solution-container">
            <div className="solution-split">
              <div className="solution-intro">
                <span className="solution-eyebrow">Ejemplo tangible</span>
                <h2>Así se ve un lead gestionado por el agente</h2>
                <p>
                  Cada mensaje se convierte en una oportunidad clara, con contexto, próxima acción y aprobación humana cuando el siguiente paso lo requiere.
                </p>
              </div>
              <div className="solution-sequence">
                {useCase.processExample.map((step, index) => (
                  <div key={step.title} className="solution-sequence__item">
                    <div className="solution-sequence__row">
                      <div className="solution-index">{String(index + 1).padStart(2, '0')}</div>
                      <div className="solution-sequence__copy">
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section
        id="como-funciona"
        className={`use-case-modules solution-section solution-modules ${useCase.processExample ? 'solution-section--canvas' : 'solution-section--mint'}`}
      >
        <div className="solution-container">
          <div className="solution-section__intro">
            <span className="solution-eyebrow">Módulos</span>
            <h2>Empieza por un proceso y amplía según impacto</h2>
            <p>Atención, integraciones, ventas, voz y marketing se combinan según las necesidades de cada proceso.</p>
          </div>
          <div className="use-case-modules__grid">
            {useCase.modules.map((module) => {
              const Icon = module.icon;
              return (
                <article key={module.title} className="use-case-modules__item">
                  <div className="use-case-modules__icon">
                    <Icon size={24} />
                  </div>
                  <h3>{module.title}</h3>
                  <p>{module.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className={`use-case-integrations solution-section solution-integrations ${useCase.processExample ? 'solution-section--mint' : 'solution-section--canvas'}`}>
        <div className="solution-container">
          <div className="solution-split">
            <div className="solution-intro">
              <span className="solution-eyebrow">Integraciones</span>
              <h2>Conectamos con tus herramientas actuales</h2>
              <p>Si ya tienes una herramienta que funciona, la mantenemos como base para que la IA atienda, clasifique, recomiende y ejecute mejor.</p>
              {useCase.key !== 'integraciones' && (
                <Link
                  to="/integraciones-software-ia/"
                  data-marketing-event="integration_click"
                  className="solution-inline-link"
                >
                  Ver landing de integraciones
                  <ArrowRight size={18} />
                </Link>
              )}
            </div>
            <div className="use-case-integrations__stack">
              <p className="use-case-integrations__status">Integraciones para tu proceso</p>
              <div className="use-case-integrations__list">
                {useCase.integrations.map((integration) => {
                  const guide = useCase.integrationGuides?.find(({ label }) => label === integration);

                  return guide ? (
                    <a
                      key={integration}
                      href={guide.href}
                      data-marketing-event="integration_click"
                      aria-label={`Ver guía de integración de ${integration}`}
                    >
                      {integration}
                    </a>
                  ) : (
                    <span key={integration}>{integration}</span>
                  );
                })}
              </div>
              <p className="use-case-integrations__disclaimer">
                <a href="https://wa.me/34614046352?text=Hola%2C%20uso%20otro%20programa%20de%20reservas%20y%20quiero%20conectarlo%20con%20WhatsApp">¿Usas otro programa de reservas? Desarrollamos la conexión para tu negocio.</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {useCase.integrationMatrix && useCase.integrationMatrix.length > 0 && (
        <section className="solution-section solution-integration-cases">
          <div className="solution-container">
            <div className="solution-section__intro">
              <span className="solution-eyebrow">Primer caso de uso</span>
              <h2>Si usas esta herramienta, empezamos por esto</h2>
<nav className="seo-links" aria-label="Agenda propia: sectores y casos"><a href="/blog/caso-exito-seven-sisters-tattoo-madrid.html">Caso Seven Sisters</a><a href="/blog/caso-exito-maker-manitas-tres-cantos.html">Caso Maker</a><a href="/blog/caso-exito-clases-particulares-lucia-whatsapp-calendario.html">Caso Lucía</a><a href="/blog/automatizacion-whatsapp-estudios-tatuaje-madrid.html">Tatuaje y piercing</a><a href="/blog/automatizacion-whatsapp-empresas-reformas-madrid.html">Servicios a domicilio</a><a href="/blog/automatizacion-whatsapp-academias-idiomas-madrid.html">Clases y academias</a></nav>
              <p>
                Definimos un primer caso de uso, una fase recomendada y, cuando existe, un recurso específico para profundizar.
              </p>
            </div>
            <div className="solution-integration-cases__grid">
              {useCase.integrationMatrix.map((row) => {
                const content = (
                  <>
                    <h3>{row.software}</h3>
                    <p>{row.firstUseCase}</p>
                    <p className="solution-integration-case__phase">{row.phase}</p>
                    {row.href && (
                      <span className="solution-integration-case__cta">
                        Ver recurso específico
                        <ArrowRight size={16} />
                      </span>
                    )}
                  </>
                );

                return row.href ? (
                  <a
                    key={row.software}
                    href={row.href}
                    data-marketing-event="integration_click"
                    className="solution-integration-case solution-integration-case--link"
                  >
                    {content}
                  </a>
                ) : (
                  <article key={row.software} className="solution-integration-case">
                    {content}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {(!useCase.featuredProof || useCase.featuredProof.length === 0) && useCase.relatedStories.length > 0 && (
        <section className="solution-section solution-section--canvas solution-related">
          <div className="solution-container">
            <div className="solution-split">
              <div className="solution-intro">
                <span className="solution-eyebrow">Casos relacionados</span>
                <h2>Ejemplos reales conectados con esta solución</h2>
                <p>
                  Antes de montar módulos nuevos, mira cómo aplicamos la misma lógica en negocios reales: conectar canales, software y procesos para reducir trabajo manual y no perder oportunidades.
                </p>
              </div>
              <div className="solution-featured-proof__stories">
                {useCase.relatedStories.map((story) => (
                  <a
                    key={story.href}
                    href={story.href}
                    data-marketing-event="case_click"
                    className="solution-story"
                  >
                    <span className="solution-story__media">
                      <picture>{useCase.key === 'integraciones' && <source type="image/webp" srcSet={story.image.replace(/\.jpg$/, '\.webp')} />}<img src={story.image} alt={story.imageAlt} loading="lazy" /></picture>
                    </span>
                    <div className="solution-story__body">
                      <h3>{story.title}</h3>
                      <p>{story.description}</p>
                      <span className="solution-story__link">
                        Ver caso
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="solution-section solution-section--mint solution-faq">
        <div className="solution-container solution-container--narrow">
          <div className="solution-section__intro">
            <span className="solution-eyebrow">Preguntas habituales</span>
            <h2>Resolvemos tus dudas</h2>
          </div>
          <div className="solution-faq__grid">
            {useCase.faq.map((item) => (
              <details key={item.q} className="solution-faq__item">
                <summary>
                  <HelpCircle aria-hidden="true" size={21} />
                  <span>{item.q}</span>
                  <ChevronDown aria-hidden="true" size={19} />
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="solution-section solution-section--canvas solution-contact">
        <div className="solution-container">
          <div className="solution-cta">
            <h2>¿Quieres verlo aplicado a tu negocio?</h2>
            <p>Dinos qué canales usas, qué software tienes y dónde se pierden más oportunidades. Te diremos qué automatizar primero y qué dejar para más adelante.</p>
            <a
              href={whatsappHref}
              data-marketing-event="pricing_cta"
              target="_blank"
              rel="noreferrer"
              className="marketing-button marketing-button--primary"
            >
              <MessageCircle size={20} />
              {useCase.cta}
            </a>
          </div>
        </div>
      </section>
        </>
      )}
    </main>
  );
};
